<template>
    <div>
        <div class="horizontal" style="height:80vh; width:90vw;">
            <div :id="'scichart-root-' + sid" style="width: 80%; margin-right: 10px;"></div>
            <ejs-grid id="sciTable" :ref="'grid-' + sid" width="20%" height="100%" :selectionSettings='selectionOptions' :allowTextWrap="true" :textWrapSettings='wrapSettings' :dataBound='dataBound' :dataSource="data" :showBusyIndicator="true" :rowDataBound="rowDataBound" :rowSelected="selectionChanged" :rowDeselected="selectionChanged">
                <e-columns>
                    <e-column type='checkbox' width='50' headerText='Discontinued' textAlign='Left' :customAttributes="customAttributes"></e-column>
                    <e-column field='title' :headerText="'Alarm type'" textAlign='Left' width="130" :customAttributes="customAttributes"></e-column>
                </e-columns>
            </ejs-grid>
        </div>
    </div>
</template>

<script>
import { SciChartSurface } from "scichart/Charting/Visuals/SciChartSurface";
import { NumericAxis } from "scichart/Charting/Visuals/Axis/NumericAxis";
import { EAxisAlignment } from "scichart/types/AxisAlignment";
import { FastLineRenderableSeries } from "scichart/Charting/Visuals/RenderableSeries/FastLineRenderableSeries";
import { XyDataSeries } from "scichart/Charting/Model/XyDataSeries";
import { ZoomPanModifier } from "scichart/Charting/ChartModifiers/ZoomPanModifier";
import { MouseWheelZoomModifier } from "scichart/Charting/ChartModifiers/MouseWheelZoomModifier";
import { RubberBandXyZoomModifier } from "scichart/Charting/ChartModifiers/RubberBandXyZoomModifier";
import { ZoomExtentsModifier } from "scichart/Charting/ChartModifiers/ZoomExtentsModifier";
import { SciChartJSLightTheme } from "scichart/Charting/Themes/SciChartJSLightTheme";
import { NumberRange } from 'scichart/Core/NumberRange';
import { RolloverModifier } from "scichart/Charting/ChartModifiers/RolloverModifier";
import { CursorModifier } from "scichart/Charting/ChartModifiers/CursorModifier";
import { EllipsePointMarker } from "scichart/Charting/Visuals/PointMarkers/EllipsePointMarker";
import { EXyDirection } from "scichart/types/XyDirection";
import { ELineDrawMode } from "scichart/Charting/Drawing/WebGlRenderContext2D"

import moment from 'moment'
import { GridPlugin, Sort, Page, Filter, Toolbar, Search, CommandColumn } from '@syncfusion/ej2-vue-grids';

export default {
    props: ['data', 'labelX', 'labelY', 'zoom', 'pan', 'showValues', 'mouseZoom', 'sid', 'initialZoomOut'],
    provide: {
        grid: [ Sort, Page, Filter, Toolbar, Search, CommandColumn  ]
    },
    data() {
        return {
            sciChartSurface: null,
            wasmContext: null,
            selection: [],
            wrapSettings: { wrapMode: 'Content' } ,
            selectionOptions: { type: 'Multiple' },
            filterOptions: {
                type: 'Menu'
            },
            customAttributes : {class: 'customcss'},
        }
    },
    methods: {
        selectionChanged() {
            this.selection = this.$refs['grid-' + this.sid].getSelectedRecords();

            if(this.data.length == 0)
                this.selection = []

            if(this.sciChartSurface != null) this.redrawScichart();
        },
        async redrawScichart() {
            this.sciChartSurface.renderableSeries.items = [];

            if(this.selection.length == 0){
                const dataSeries = new XyDataSeries(this.wasmContext, {
                    xValues: {},
                    yValues: {}
                })

                const lineSeries = new FastLineRenderableSeries(this.wasmContext, {
                    dataSeries
                })

                this.sciChartSurface.renderableSeries.add(lineSeries);
            }

            this.selection.forEach( (e, index) => {

                var d = [];
                e.yValues.forEach((dt, i) => {
                    d.push(new Date(moment(dt, "DD.MM.YYYY HH.mm.ss")).getTime())
                })

                var v = [];
                e.xValues.forEach((dt) => {
                    v.push(dt)
                })

                const dataSeries = new XyDataSeries(this.wasmContext, {
                    xValues: d,
                    yValues: v
                })

                dataSeries.dataSeriesName = e.title;

                const lineSeries = new FastLineRenderableSeries(this.wasmContext, {
                    dataSeries,
                    stroke: e.strokeColor,
                    pointMarker: new EllipsePointMarker(this.wasmContext, {
                        width: 3,
                        height: 3,
                        strokeThickness: 1,
                        fill: e.strokeColor + "30",
                        stroke: e.strokeColor
                    })
                })

                lineSeries.stroke = e.strokeColor
                lineSeries.rolloverModifierProps.tooltipTitle = e.title
                lineSeries.rolloverModifierProps.tooltipTextColor = '#fff'
                lineSeries.rolloverModifierProps.tooltipColor = e.strokeColor;
                lineSeries.rolloverModifierProps.tooltipLabelX = this.labelX ? this.labelX : "X";
                // lineSeries.rolloverModifierProps.tooltipLabelY = this.labelY ? this.labelY : "Y";
                this.sciChartSurface.renderableSeries.add(lineSeries);
            })

            if(this.selection.length != 0){
                this.sciChartSurface.zoomExtents();
            }

        },
        async initSciChart(){
            const {sciChartSurface, wasmContext} = await SciChartSurface.create("scichart-root-" + this.sid);

            this.sciChartSurface = sciChartSurface;
            this.wasmContext = wasmContext;

            await sciChartSurface.applyTheme(new SciChartJSLightTheme())

            const xAxis = new NumericAxis(this.wasmContext, { axisAlignment: EAxisAlignment.Top});
            const yAxis = new NumericAxis(this.wasmContext, { axisAlignment: EAxisAlignment.Left});

            if(this.initialZoomOut == true){
                yAxis.growBy = new NumberRange(0.1, 0.1);
                xAxis.growBy = new NumberRange(0.1, 0.1);
            }

            this.sciChartSurface.xAxes.add(xAxis)
            this.sciChartSurface.yAxes.add(yAxis)

            xAxis.labelProvider.formatLabel = (unixTimestamp) => {
                return moment(unixTimestamp).format("DD.MM, HH:mm:ss")
            };

            this.data.forEach( (e, index) => {

                var d = [];
                e.yValues.forEach((dt, i) => {
                    d.push(new Date(moment(dt, "DD.MM.YYYY HH.mm.ss")).getTime())
                })

                var v = [];
                e.xValues.forEach((dt) => {
                    v.push(dt)
                })

                const dataSeries = new XyDataSeries(this.wasmContext, {
                    xValues: d,
                    yValues: v
                })

                dataSeries.dataSeriesName = e.title;

                const lineSeries = new FastLineRenderableSeries(this.wasmContext, {
                    dataSeries,
                    drawNaNAs: ELineDrawMode.DiscontinuousLine,
                    stroke: e.strokeColor,
                    strokeThickness: 3,
                    pointMarker: new EllipsePointMarker(this.wasmContext, {
                        width: 4,
                        height: 4,
                        strokeThickness: 1,
                        fill: e.strokeColor + "30",
                        stroke: e.strokeColor
                    })
                })

                lineSeries.stroke = e.strokeColor
                lineSeries.rolloverModifierProps.tooltipTitle = e.title
                lineSeries.rolloverModifierProps.tooltipTextColor = '#fff'
                lineSeries.rolloverModifierProps.tooltipColor = e.strokeColor;
                lineSeries.rolloverModifierProps.tooltipLabelX = this.labelX ? this.labelX : "X";
                // lineSeries.rolloverModifierProps.tooltipLabelY = this.labelY ? this.labelY : "Y";
                this.sciChartSurface.renderableSeries.add(lineSeries);

            })

            if(this.zoomBySelection == true) this.sciChartSurface.chartModifiers.add(new RubberBandXyZoomModifier({ xyDirection: EXyDirection.XDirection }));
            if(this.pan != false ) this.sciChartSurface.chartModifiers.add(new ZoomPanModifier({ xyDirection: EXyDirection.XDirection }));
            if(this.zoom != false) this.sciChartSurface.chartModifiers.add(new ZoomExtentsModifier({ xyDirection: EXyDirection.XDirection }));
            if(this.mouseZoom != false) this.sciChartSurface.chartModifiers.add(new MouseWheelZoomModifier({ xyDirection: EXyDirection.XDirection }));
            const rolloverModifier = new RolloverModifier();
            rolloverModifier.showRolloverLine = false;
            this.sciChartSurface.chartModifiers.add(rolloverModifier);

            this.sciChartSurface.chartModifiers.add(new CursorModifier({
                crosshairStroke: "#0095B2",
                crosshairStrokeThickness: 1,
                tooltipContainerBackground: "#000",
                tooltipTextStroke: "#0095B2",
                showTooltip: false,
                axisLabelFill: "#0095B2",
                axisLabelStroke: "#fff",
            }))

            this.sciChartSurface.zoomExtents();

        },
        applyStyles(){
            this.sciChartSurface.applyTheme({
                annotationsGripsBackroundBrush: "white",
                annotationsGripsBorderBrush: "white",
                axis3DBandsFill: "#1F3D6833",
                axisBandsFill: "#1F3D6833",
                axisPlaneBackgroundFill: "Transparent",
                columnFillBrush: "white",
                columnLineColor: "white",
                cursorLineBrush: "#6495ED99",
                defaultColorMapBrush: [
                    { offset: 0, color: "DarkBlue" },
                    { offset: 0.5, color: "CornflowerBlue" },
                    { offset: 1, color: "#FF22AA" }
                ],
                downBandSeriesFillColor: "#6495ED99", // "#52CC5490",
                downBandSeriesLineColor: "#E26565FF",
                downBodyBrush: "white",
                downWickColor: "white",
                gridBackgroundBrush: "white",
                gridBorderBrush: "white",
                labelBackgroundBrush: "#6495EDAA",
                labelBorderBrush: "#6495ED",
                labelForegroundBrush: "#EEEEEE",
                legendBackgroundBrush: "#1D2C35",
                lineSeriesColor: "#0095B2",
                majorGridLineBrush: "#1F3D68",
                minorGridLineBrush: "#102A47",
                mountainAreaBrush: "white",
                mountainLineColor: "white",
                overviewFillBrush: "white",
                planeBorderColor: "white",
                rolloverLineBrush: "#FD9F2533",
                rubberBandFillBrush: "#99999933",
                rubberBandStrokeBrush: "#99999977",
                sciChartBackground: "#0D213A",
                scrollbarBackgroundBrush: "white",
                scrollbarBorderBrush: "white",
                scrollbarGripsBackgroundBrush: "white",
                scrollbarViewportBackgroundBrush: "white",
                scrollbarViewportBorderBrush: "white",
                shadowEffectColor: "white",
                textAnnotationBackground: "#6495EDAA",
                textAnnotationForeground: "#EEEEEE",
                tickTextBrush: "#6495ED",
                upBandSeriesFillColor: "white",
                upBandSeriesLineColor: "white",
                upBodyBrush: "#6495EDA0",
                upWickColor: "#6495ED"
            });
        },
        dataBound: function() {
            this.$refs['grid-' + this.sid].autoFitColumns();
            var rowIndexes = [];
            this.data.forEach((e,i) => {
                rowIndexes.push(i);
            })
            this.$refs['grid-' + this.sid].selectRows(rowIndexes);
        },
        rowDataBound: function(args) {
            args.row.classList.add('whiteText');
            args.row.style.backgroundColor = args.data['strokeColor'];
        },
    },
    mounted: async function() {
        this.initSciChart();
    },
    beforeDestroy: async function() {
        if(this.sciChartSurface || this.wasmContext){
            this.sciChartSurface = null
            this.wasmContext = null
        }
        this.sciChartSurface = null
        this.wasmContext = null
    },
    updated: async function(){
        if(this.data.length == 0){
            this.selectionChanged()
        }
    }
}
</script>

<style>
#sciTable.e-grid .e-selectionbackground.e-rowcell {
    background-color: rgba(255, 255, 255, 0.05);
}

#sciTable.e-grid .e-row.whiteText .e-rowcell {
    color: #fff;
    font-weight: 600;
    font-size: 13px;
    line-height: 16px;
}
</style>



// WEBPACK FOOTER //
// src/components/SciChartMultiple.vue